import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import {
  ACCESS_TIME_STATUS,
  type BookingSummary,
  type ShortReservation,
} from '@chekin/core';
import {getConditionLeadHours} from './lockConditions';

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

function getLockNow() {
  const now = dayjs.utc(new Date());
  const offset = dayjs().tz(dayjs.tz.guess()).utcOffset();
  return dayjs(now.add(offset / 60, 'hour'));
}

export function checkIfCurrentDayBetween(startTime?: string, endTime?: string): boolean {
  if (!startTime || !endTime) return false;
  return getLockNow().isBetween(dayjs(startTime), dayjs(endTime));
}

export function isLockTimePast(time?: string): boolean {
  if (!time) return false;
  return getLockNow().isAfter(dayjs(time));
}

export function getSecondsUntilLockTime(time?: string): number {
  if (!time) return 0;
  return Math.max(0, dayjs(time).diff(getLockNow(), 'second'));
}

// Check-in, access and lock times arrive as the property's wall-clock time labelled
// +00:00, so they are read and formatted in UTC to keep that wall-clock time.
export function getConditionGoLiveAt(condition: string, checkInDate?: string) {
  const leadHours = getConditionLeadHours(condition);
  if (leadHours === undefined || !checkInDate) return undefined;
  return dayjs.utc(checkInDate).subtract(leadHours, 'hour');
}

export function getKeysGoLive(conditions: string[] = [], checkInDate?: string) {
  const leadHours = conditions
    .map(getConditionLeadHours)
    .filter((hours): hours is number => hours !== undefined);

  if (!leadHours.length || !checkInDate) return undefined;

  const closest = Math.min(...leadHours);
  return {at: dayjs.utc(checkInDate).subtract(closest, 'hour'), leadHours: closest};
}

export function getAccessOpensAt(
  summary: BookingSummary | undefined,
  reservation: ShortReservation | undefined,
) {
  const {checkInDate, startTime} = getAccessWindow(summary, reservation);
  if (!checkInDate || !startTime) return undefined;
  return dayjs.utc(`${checkInDate}T${startTime}`);
}

export function formatLockTime(value: string, format: string): string {
  return dayjs.utc(value).format(format);
}

export const isNoHours = (summary?: BookingSummary) =>
  Boolean(
    !summary?.is_remote_access_enabled ||
      summary?.access_time_status === ACCESS_TIME_STATUS.disabled,
  );

export function getAccessWindow(
  summary: BookingSummary | undefined,
  reservation: ShortReservation | undefined,
): {
  startTime: string | undefined;
  endTime: string | undefined;
  checkInDate: string | undefined;
  checkOutDate: string | undefined;
} {
  const hasHours = !isNoHours(summary);

  if (summary?.access_time_status === ACCESS_TIME_STATUS.custom) {
    return {
      startTime: hasHours ? summary.access_start_time : undefined,
      endTime: hasHours ? summary.access_end_time : undefined,
      checkInDate: summary.check_in_date?.slice(0, 10),
      checkOutDate: summary.check_out_date?.slice(0, 10),
    };
  }
  return {
    startTime: hasHours ? reservation?.access_start_time : undefined,
    endTime: hasHours ? reservation?.access_end_time : undefined,
    checkInDate: reservation?.check_in_date?.slice(0, 10),
    checkOutDate: reservation?.check_out_date?.slice(0, 10),
  };
}
